import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, StickyNote } from "lucide-react";
import { format, parseISO } from "date-fns";
import { TaskStatusBadge, TaskPriorityBadge, LinkTypeChip } from "@/components/TaskBadge";

const RECORD_PATH = { Asset: "assets", Lead: "leads", Broker: "brokers" };
const CLOSED = ["Completed", "Cancelled"];

function DueCell({ task }) {
  if (!task.due_date) return <span className="text-slate-400">—</span>;
  const d = parseISO(task.due_date);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(d); due.setHours(0, 0, 0, 0);
  const open = !CLOSED.includes(task.status);
  let cls = "text-slate-700";
  let testid = "task-due-normal";
  if (open && due < today) { cls = "text-[#A85A3C] font-semibold"; testid = "task-due-overdue"; }
  else if (open && due.getTime() === today.getTime()) { cls = "text-[#A67C3D] font-semibold"; testid = "task-due-today"; }
  return <span className={cls} data-testid={testid}>{format(d, "dd-MM-yyyy")}</span>;
}

export default function TaskTable({ tasks = [], onEdit, hideLinked = false, emptyText = "No tasks." }) {
  const navigate = useNavigate();
  if (tasks.length === 0) {
    return <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="tasks-empty">{emptyText}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="data-table w-full text-left">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="label-eyebrow !text-slate-600">Task Title</th>
            {!hideLinked && <th className="label-eyebrow !text-slate-600">Linked Record</th>}
            <th className="label-eyebrow !text-slate-600">Priority</th>
            <th className="label-eyebrow !text-slate-600">Assigned To</th>
            <th className="label-eyebrow !text-slate-600">Due Date</th>
            {!hideLinked && <th className="label-eyebrow !text-slate-600">Reminder</th>}
            <th className="label-eyebrow !text-slate-600">Status</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => (
            <tr
              key={t.id}
              className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
              onClick={() => onEdit(t)}
              data-testid={`task-row-${t.id}`}
            >
              <td className="max-w-[260px]">
                <div className="flex items-center gap-1.5">
                  <div className="text-sm font-semibold text-slate-900 truncate">{t.title}</div>
                  {t.source_note_id && (
                    t.source_note_deleted ? (
                      <span title="Originating note deleted" data-testid={`task-from-note-icon-deleted-${t.id}`}>
                        <StickyNote className="h-3.5 w-3.5 shrink-0 text-slate-300" />
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/notes?highlight=${t.source_note_id}`); }}
                        className="shrink-0 text-rose-500 hover:text-rose-700"
                        title={t.source_note_label ? `From note: ${t.source_note_label}` : "From note"}
                        data-testid={`task-from-note-icon-${t.id}`}
                      >
                        <StickyNote className="h-3.5 w-3.5" />
                      </button>
                    )
                  )}
                </div>
                {t.description && (
                  <div className="text-[11px] text-slate-500 truncate">{t.description.slice(0, 60)}{t.description.length > 60 ? "…" : ""}</div>
                )}
              </td>
              {!hideLinked && (
                <td>
                  {t.linked_record_type && t.linked_record_id ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); navigate(`/${RECORD_PATH[t.linked_record_type]}/${t.linked_record_id}`); }}
                      className="inline-flex items-center gap-1.5 hover:underline"
                      data-testid={`task-linked-link-${t.id}`}
                    >
                      <LinkTypeChip type={t.linked_record_type} />
                      <span className="text-sm text-slate-700 truncate max-w-[140px]">{t.linked_record_name || "—"}</span>
                    </button>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              )}
              <td><TaskPriorityBadge priority={t.priority} /></td>
              <td className="text-sm text-slate-700">{t.assigned_to_name || "—"}</td>
              <td className="text-sm"><DueCell task={t} /></td>
              {!hideLinked && (
                <td>
                  {t.reminder_datetime ? (
                    <span className="inline-flex items-center gap-1 text-slate-500" title={format(parseISO(t.reminder_datetime), "dd-MM-yyyy HH:mm")} data-testid={`task-reminder-${t.id}`}>
                      <Clock className="h-3.5 w-3.5" />
                      <span className="text-[11px]">{format(parseISO(t.reminder_datetime), "dd MMM HH:mm")}</span>
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              )}
              <td><TaskStatusBadge status={t.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
